import React, { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { apiJson } from '../../lib/api'
import { openableRow } from '../../lib/openableRow'
import { PageBody, PageHeader } from '../../components/PageHeader'
import { dateTimeText, EmailDetail, EmailRow, EmailsResponse, EmailStatus, Rating } from './api'
import { StatusPill } from './DashboardPage'
import styles from './GodMode.module.css'
import own from './EmailsPage.module.css'

// Queued email: every email CONTROL kept (API services/email-outbox.js). Those
// not sent yet first, each with Retry; then every email, newest first, by
// client (System is Dataphyll's own), badge, status and words in the subject,
// the body or the address. A failed email is tried again 1 min, 5 min, 1 h and
// 3 h on, then it is Failed; Retry sends it again now.

export const KIND_LABEL: Record<string, string> = {
  scheduled_report: 'Scheduled report',
  invite: 'Invite',
  crew_query: 'Crew query',
}
const kindLabel = (k: string) => KIND_LABEL[k] || k.replace(/_/g, ' ')

const STATUS: Record<EmailStatus, { rating: Rating; label: string }> = {
  queued: { rating: 'idle', label: 'Queued' },
  sending: { rating: 'idle', label: 'Sending' },
  retrying: { rating: 'warn', label: 'Retrying' },
  sent: { rating: 'ok', label: 'Sent' },
  failed: { rating: 'bad', label: 'Failed' },
}
// Four retries after the first attempt (RETRY_DELAYS_MIN).
const MAX_ATTEMPTS = 5

const timeText = (at: string) => new Date(at).toLocaleTimeString('en-NZ', { hour: 'numeric', minute: '2-digit' })

function attemptText(e: EmailRow | EmailDetail) {
  if (e.status === 'sent') return e.attempts > 1 ? `on attempt ${e.attempts}` : null
  if (e.status === 'retrying' && e.next_attempt_at) return `attempt ${e.attempts} of ${MAX_ATTEMPTS} failed · next ${timeText(e.next_attempt_at)}`
  if (e.status === 'failed') return `after ${e.attempts} attempt${e.attempts === 1 ? '' : 's'}`
  return null
}

function Badge({ kind }: { kind: string }) {
  return <span className={`${own.badge} ${own[`kind_${kind}`] || ''}`}>{kindLabel(kind)}</span>
}

interface Filter {
  tenant: string
  kind: string
  status: string
  q: string
}

const query = (f: Filter, page: number, status = f.status) =>
  new URLSearchParams(Object.entries({ tenant: f.tenant, kind: f.kind, status, q: f.q.trim(), page: String(page) }).filter(([, v]) => v)).toString()

// embedded: the Emails tab of Queues, under its header.
export function EmailsPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { user } = useAuth()
  const [filter, setFilter] = useState<Filter>({ tenant: '', kind: '', status: '', q: '' })
  const [words, setWords] = useState('')
  const [page, setPage] = useState(1)
  const [data, setData] = useState<EmailsResponse | null>(null)
  const [unsent, setUnsent] = useState<EmailsResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<string | null>(null)
  const [detail, setDetail] = useState<EmailDetail | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const [all, waiting] = await Promise.all([
        apiJson<EmailsResponse>(user, `/api/god-mode/emails?${query(filter, page)}`),
        apiJson<EmailsResponse>(user, `/api/god-mode/emails?${query({ ...filter, status: '' }, 1, 'unsent')}`),
      ])
      setData(all)
      setUnsent(waiting)
      setError(null)
    } catch (e: any) {
      setError(e.message)
    }
  }, [user, filter, page])

  useEffect(() => {
    load()
    // Retries happen on their own: keep the page current while it is open.
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') load()
    }, 30000)
    return () => clearInterval(t)
  }, [load])

  // Searching waits for a pause in typing.
  useEffect(() => {
    const t = setTimeout(() => {
      setFilter((f) => (f.q === words ? f : { ...f, q: words }))
      setPage(1)
    }, 300)
    return () => clearTimeout(t)
  }, [words])

  const set = (k: keyof Filter) => (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilter((f) => ({ ...f, [k]: e.target.value }))
    setPage(1)
  }

  const where = (e: EmailRow) => e.tenant?.id || 'system'

  // An unsent email is in both tables: it opens in the one it was clicked in.
  const toggle = async (e: EmailRow, key: string) => {
    if (open === key) {
      setOpen(null)
      return
    }
    setOpen(key)
    setDetail(null)
    try {
      setDetail((await apiJson<{ email: EmailDetail }>(user, `/api/god-mode/emails/${where(e)}/${e.id}`)).email)
    } catch (err: any) {
      setError(err.message)
    }
  }

  const retry = async (e: EmailRow) => {
    setBusy(e.id)
    setNote(null)
    setError(null)
    try {
      const r = await apiJson<{ sent: boolean; reason: string | null; email: EmailDetail }>(user, `/api/god-mode/emails/${where(e)}/${e.id}/retry`, { method: 'POST' })
      setNote(r.sent ? `Sent to ${e.to_address}.` : `Not sent to ${e.to_address}: ${r.reason}`)
      if (open?.endsWith(`:${e.id}`)) setDetail(r.email)
      await load()
    } catch (err: any) {
      setError(err.message)
      await load()
    } finally {
      setBusy(null)
    }
  }

  const counts = data?.counts
  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1

  const table = (rows: EmailRow[], label: string) => (
    <table className={styles.table} aria-label={label}>
      <thead>
        <tr>
          <th>When</th>
          <th>Type</th>
          <th>Client</th>
          <th>To</th>
          <th>Subject</th>
          <th>Status</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((e) => (
          <React.Fragment key={e.id}>
            <tr className={own.row} {...openableRow(() => toggle(e, `${label}:${e.id}`))} data-testid={`email-${e.id}`} aria-expanded={open === `${label}:${e.id}`}>
              <td className={own.nowrap}>{dateTimeText(e.created_at)}</td>
              <td><Badge kind={e.kind} /></td>
              <td>{e.tenant ? e.tenant.name : <span className={own.system}>System</span>}</td>
              <td>{e.to_address}</td>
              <td>
                {e.subject}
                {e.has_attachments && <span className={own.sub}> · attachment</span>}
              </td>
              <td>
                <StatusPill status={STATUS[e.status].rating}>{STATUS[e.status].label}</StatusPill>
                {attemptText(e) && <div className={own.sub}>{attemptText(e)}</div>}
                {e.status !== 'sent' && e.last_error && <div className={own.sub}>{e.last_error}</div>}
              </td>
              <td>
                {e.status !== 'sent' && (
                  <button
                    type="button"
                    className={styles.secondary}
                    disabled={busy === e.id}
                    onClick={(ev) => {
                      ev.stopPropagation()
                      retry(e)
                    }}
                  >
                    {busy === e.id ? 'Sending…' : 'Retry'}
                  </button>
                )}
              </td>
            </tr>
            {open === `${label}:${e.id}` && (
              <tr className={own.open}>
                <td colSpan={7}>
                  {!detail ? (
                    'Loading…'
                  ) : (
                    <div className={own.detail}>
                      <dl className={styles.facts}>
                        <dt>Attempts</dt>
                        <dd>{detail.attempts}{detail.last_attempt_at ? `, last ${dateTimeText(detail.last_attempt_at)}` : ''}</dd>
                        {detail.sent_at && (
                          <>
                            <dt>Sent</dt>
                            <dd>{dateTimeText(detail.sent_at)}</dd>
                          </>
                        )}
                        {detail.last_error && (
                          <>
                            <dt>Last error</dt>
                            <dd>{detail.last_error}</dd>
                          </>
                        )}
                        {detail.retried_by && (
                          <>
                            <dt>Sent again by</dt>
                            <dd>{detail.retried_by}</dd>
                          </>
                        )}
                        {detail.attachments.length > 0 && (
                          <>
                            <dt>Attached</dt>
                            <dd>{detail.attachments.join(', ')}</dd>
                          </>
                        )}
                      </dl>
                      <pre className={own.body}>{detail.text_body}</pre>
                    </div>
                  )}
                </td>
              </tr>
            )}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  )

  const tile = (label: string, n: number, status: string, rating: Rating) => (
    <button
      type="button"
      className={`${own.tile} ${filter.status === status ? own.tileOn : ''}`}
      aria-pressed={filter.status === status}
      onClick={() => {
        setFilter((f) => ({ ...f, status: f.status === status ? '' : status }))
        setPage(1)
      }}
    >
      <span className={own.tileLabel}>{label}</span>
      <StatusPill status={n ? rating : 'idle'}>{n}</StatusPill>
    </button>
  )

  const body = (
        <div className={styles.stack}>
          <div className={own.filters}>
            <label className={styles.field}>
              Client
              <select value={filter.tenant} onChange={set('tenant')}>
                <option value="">All clients and System</option>
                <option value="system">System (Dataphyll)</option>
                {data?.clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              Type
              <select value={filter.kind} onChange={set('kind')}>
                <option value="">All types</option>
                {(data?.kinds || Object.keys(KIND_LABEL)).map((k) => (
                  <option key={k} value={k}>{kindLabel(k)}</option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              Status
              <select value={filter.status} onChange={set('status')}>
                <option value="">All</option>
                <option value="unsent">Not sent yet</option>
                <option value="queued">Queued</option>
                <option value="sending">Sending</option>
                <option value="retrying">Retrying</option>
                <option value="failed">Failed</option>
                <option value="sent">Sent</option>
              </select>
            </label>
            <label className={`${styles.field} ${own.search}`}>
              Search
              <input type="search" value={words} onChange={(e) => setWords(e.target.value)} placeholder="Subject, body or address" maxLength={200} />
            </label>
          </div>

          {counts && (
            <div className={own.tiles}>
              {tile('Queued', counts.queued + counts.sending, 'queued', 'idle')}
              {tile('Retrying', counts.retrying, 'retrying', 'warn')}
              {tile('Failed', counts.failed, 'failed', 'bad')}
              {tile('Sent', counts.sent, 'sent', 'ok')}
            </div>
          )}

          {error && <p className={styles.error}>{error}</p>}
          {note && <p className={styles.saved}>{note}</p>}
          {!data && !error && <p className={styles.lead}>Loading…</p>}

          {unsent && unsent.emails.length > 0 && filter.status === '' && (
            <>
              <h2 className={own.heading}>Not sent yet ({unsent.total})</h2>
              <div className={styles.tableCard}>{table(unsent.emails, 'Not sent yet')}</div>
            </>
          )}

          {data && (
            <>
              <h2 className={own.heading}>{filter.status ? 'Emails' : 'All emails'} ({data.total})</h2>
              <div className={styles.tableCard}>
                {data.emails.length === 0 ? <div className={styles.empty}>No emails match.</div> : table(data.emails, 'Emails')}
              </div>
              {pages > 1 && (
                <div className={own.pager}>
                  <button type="button" className={styles.secondary} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                    Previous
                  </button>
                  <span>Page {page} of {pages}</span>
                  <button type="button" className={styles.secondary} disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
                    Next
                  </button>
                </div>
              )}
            </>
          )}
          <p className={own.sub}>
            An email that fails is tried again 1 min, 5 min, 1 h and 3 h later, then it is Failed. Retry sends it again now. Bodies hold their
            invite and report links, so this page is Dataphyll staff only.
          </p>
        </div>
  )
  if (embedded) return body
  return (
    <>
      <PageHeader title="Queued email" />
      <PageBody>{body}</PageBody>
    </>
  )
}
